import React, { useLayoutEffect, useMemo, useState } from 'react';
import {
  NavigationType,
  Router,
  createPath,
  parsePath,
  type Location,
  type Navigator,
  type To,
} from 'react-router-dom';
import { resolveOrgPath } from '@/lib/org-path';

/**
 * BrowserRouter, except that a link or navigation to a workspace page written
 * in the short form ("/college/students") is sent to this tab's organization
 * ("/college/<slug>/students"). It applies to rendered hrefs too, so opening a
 * link in a new tab or copying it keeps the organization.
 *
 * Kept deliberately close to BrowserRouter: history entries carry the same
 * `{ usr, key, idx }` state shape, so back/forward and location.state behave
 * exactly as before.
 */

interface HistoryEntryState {
  usr: unknown;
  key: string;
  idx: number;
}

function readLocation(): Location {
  const entry = window.history.state as Partial<HistoryEntryState> | null;
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    state: entry?.usr ?? null,
    key: entry?.key ?? 'default',
  };
}

const newKey = () => Math.random().toString(36).slice(2, 10);

function resolve(to: To): string {
  const path = typeof to === 'string' ? parsePath(to) : to;
  const pathname = path.pathname ?? window.location.pathname;
  return createPath({ ...path, pathname: resolveOrgPath(pathname) });
}

export const OrgAwareRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [nav, setNav] = useState(() => ({
    action: NavigationType.Pop,
    location: readLocation(),
  }));

  useLayoutEffect(() => {
    // Give the first entry the same shape later entries get.
    const entry = window.history.state as Partial<HistoryEntryState> | null;
    if (entry?.idx == null) {
      window.history.replaceState({ usr: entry?.usr ?? null, key: newKey(), idx: 0 }, '');
    }
    const onPop = () => setNav({ action: NavigationType.Pop, location: readLocation() });
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigator = useMemo<Navigator>(() => {
    const idx = () => (window.history.state as Partial<HistoryEntryState> | null)?.idx ?? 0;
    return {
      createHref: (to) => resolve(to),
      encodeLocation: (to) => {
        const url = new URL(resolve(to), window.location.origin);
        return { pathname: url.pathname, search: url.search, hash: url.hash };
      },
      go: (delta) => window.history.go(delta),
      push: (to, state) => {
        window.history.pushState(
          { usr: state ?? null, key: newKey(), idx: idx() + 1 },
          '',
          resolve(to)
        );
        setNav({ action: NavigationType.Push, location: readLocation() });
      },
      replace: (to, state) => {
        window.history.replaceState(
          { usr: state ?? null, key: newKey(), idx: idx() },
          '',
          resolve(to)
        );
        setNav({ action: NavigationType.Replace, location: readLocation() });
      },
    };
  }, []);

  return (
    <Router location={nav.location} navigationType={nav.action} navigator={navigator}>
      {children}
    </Router>
  );
};
